import { useEffect, useState } from "react";
import axios from "axios";
import useTranslator from "../../hooks/useTranslator";
import SkorLogo from "../../assets/img/skorLogo.svg";
import "./BeneficiaryInsurancePayment.css";

const STATUS = {
  LOADING: "loading",
  REDIRECTING: "redirecting",
  SUCCESS: "success",
  FAILURE: "failure",
  ERROR: "error",
};

const CenterLayout = ({ children }) => (
  <div className="bip-page">
    <div className="bip-card">{children}</div>
  </div>
);

const SpinnerIcon = () => (
  <div className="bip-spinner-wrap">
    <svg
      className="bip-spinner"
      width="42"
      height="42"
      viewBox="0 0 42 42"
      fill="none"
    >
      <circle cx="21" cy="21" r="17" stroke="#e8eceb" strokeWidth="4" />
      <path
        d="M38 21a17 17 0 0 0-17-17"
        stroke="#253837"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  </div>
);

const StatusIcon = ({ type }) => {
  const isSuccess = type === "success";
  return (
    <div className={`bip-status-icon ${isSuccess ? "success" : "error"}`}>
      {isSuccess ? (
        <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
          <path
            d="M10.5 17.5l5 5 9-10"
            stroke="#253837"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
          <path
            d="M11 11l12 12M23 11L11 23"
            stroke="#c0392b"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
      )}
    </div>
  );
};

const ActionButton = ({ children, onClick }) => (
  <button className="bip-btn" onClick={onClick}>
    {children}
  </button>
);

const BeneficiaryInsurancePayment = () => {
  const { _t } = useTranslator();

  const [status, setStatus] = useState(STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const paymentStatus = params.get("status");

    window.history.replaceState({}, "", window.location.pathname);

    if (paymentStatus === "success") {
      setStatus(STATUS.SUCCESS);
      return;
    }

    if (paymentStatus === "failure") {
      setStatus(STATUS.FAILURE);
      return;
    }

    if (!token) {
      setStatus(STATUS.ERROR);
      setErrorMessage(_t("invalidOrExpiredPaymentLink"));
      return;
    }

    initiatePayment(token);
  }, []);

  const initiatePayment = async (token) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/insurance/beneficiary/initiate-payment`,
        { token },
        {
          headers: {
            "Accept-Language": localStorage.getItem("i18nextLng") || "en",
          },
        }
      );

      const paymentUrl = res.data?.success_res?.paymentUrl;

      if (!paymentUrl) {
        setStatus(STATUS.ERROR);
        setErrorMessage(_t("paymentLinkNotReceived"));
        return;
      }

      setStatus(STATUS.REDIRECTING);
      window.location.href = paymentUrl;
    } catch {
      setStatus(STATUS.ERROR);
      setErrorMessage(_t("paymentLinkIsInvalidOrExpired"));
    }
  };

  if (status === STATUS.LOADING || status === STATUS.REDIRECTING) {
    return (
      <CenterLayout>
        <SpinnerIcon />
        <h2 className="bip-title">
          {status === STATUS.REDIRECTING
            ? _t("redirectingToPayment")
            : _t("verifyingYourPaymentLink")}
        </h2>
        <p className="bip-text">
          {_t(
            "pleaseWaitWhileWeSecurelyProcessYourPayment",
            "Please wait while we securely process your payment."
          )}
        </p>
      </CenterLayout>
    );
  }

  if (status === STATUS.SUCCESS) {
    return (
      <CenterLayout>
        <StatusIcon type="success" />
        <h2 className="bip-title">{_t("paymentSuccessful")}</h2>
        <p className="bip-text">
          {_t(
            "yourPaymentHasBeenCompletedSuccessfully",
            "Your payment has been completed successfully."
          )}
        </p>
      </CenterLayout>
    );
  }

  if (status === STATUS.FAILURE) {
    return (
      <CenterLayout>
        <StatusIcon type="error" />
        <h2 className="bip-title">{_t("paymentNotCompleted")}</h2>
        <p className="bip-text">
          {_t(
            "yourPaymentCouldNotBeCompletedPleaseTryAgain",
            "Your payment could not be completed. Please try again."
          )}
        </p>
      </CenterLayout>
    );
  }

  // ERROR state
  return (
    <CenterLayout>
      <img className="bip-logo" src={SkorLogo} alt="Skor" />
      <h2 className="bip-title">{_t("unableToProcessPayment")}</h2>
      <p className="bip-text bip-error-text">{errorMessage}</p>
    </CenterLayout>
  );
};

export default BeneficiaryInsurancePayment;
